import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Brain,
  Stethoscope,
  HelpCircle,
  ArrowRight,
  ChevronDown,
  Sparkles,
  ShieldCheck,
  Layers,
} from "lucide-react";


export const Route = createFileRoute("/therapy-approaches")({
  head: () => ({
    meta: [
      { title: "Therapy Approaches — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A trauma-informed hub of therapy approaches — from EMDR and IFS to somatic, creative, and holistic modalities.",
      },
      { property: "og:title", content: "Therapy Approaches — Recovery Trauma™" },
      { property: "og:description", content: "A trauma-informed hub of therapy approaches — from EMDR and IFS to somatic, creative, and holistic modalities." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/therapy-approaches" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/therapy-approaches" }],
  }),
  component: TherapyApproachesPage,
});

type Evidence =
  | "🟢 Evidence-Based"
  | "🟡 Emerging Evidence"
  | "🔵 Complementary Approach";

type Section = { heading: string; body?: string; bullets?: string[] };

type Modality = {
  name: string;
  tagline?: string;
  evidence?: Evidence;
  developedBy?: string;
  sections: Section[];
};

type Category = {
  emoji: string;
  title: string;
  intro?: string;
  items: Modality[];
};

const CATEGORIES: Category[] = [
  {
    emoji: "🧠",
    title: "TRAUMA PROCESSING THERAPIES",
    intro:
      "These therapies are specifically designed to help people process traumatic experiences and reduce the impact trauma has on daily life.",
    items: [
      {
        name: "EMDR (Eye Movement Desensitization and Reprocessing)",
        tagline: "The brain can finish processing what once felt too much.",
        evidence: "🟢 Evidence-Based",
        developedBy: "Dr. Francine Shapiro (1987)",
        sections: [
          {
            heading: "What is it?",
            body:
              "EMDR was developed after Francine Shapiro noticed that certain eye movements appeared to reduce the emotional intensity of distressing thoughts. Since then, EMDR has become one of the most researched trauma therapies in the world.",
          },
          {
            heading: "How does it work?",
            body:
              "When trauma occurs, memories can become \"stuck\" in the brain along with the emotions, body sensations, and beliefs attached to the experience. EMDR uses bilateral stimulation (such as eye movements, tapping, or sounds) while revisiting memories in a safe and structured way. This helps the brain continue a natural healing process that may have been interrupted during the traumatic event.",
          },
          {
            heading: "What might change?",
            body:
              "Many people report that traumatic memories remain remembered but no longer feel emotionally overwhelming.",
          },
          {
            heading: "Commonly used for",
            bullets: ["PTSD", "Complex PTSD", "Childhood trauma", "Anxiety", "Grief", "Phobias", "Medical trauma"],
          },
        ],
      },
      {
        name: "Internal Family Systems (IFS)",
        tagline: "Every part of you has a reason for being there.",
        evidence: "🟡 Emerging Evidence",
        developedBy: "Dr. Richard Schwartz",
        sections: [
          {
            heading: "What is it?",
            body:
              "Rather than seeing parts of yourself as problems, IFS helps you understand the different protective, wounded, and adaptive parts that developed throughout your life. The goal is to build a compassionate relationship with these parts and help them feel heard, understood, and less burdened.",
          },
          {
            heading: "Parts you may meet",
            bullets: [
              "Inner critic parts",
              "People-pleasing parts",
              "Protective parts",
              "Younger wounded parts (inner child)",
              "Angry or controlling parts",
            ],
          },
          {
            heading: "How does it work?",
            body:
              "IFS believes every part developed for a reason and is trying to help, even when its strategy now creates difficulty. Instead of fighting parts of yourself, you slowly turn toward them with curiosity, build trust, and help them release the burdens they have carried — sometimes for decades.",
          },
          {
            heading: "What a session may involve",
            bullets: [
              "Pausing to notice which part is most present",
              "Sensing where you feel the part in your body",
              "Listening to what the part is afraid of, or trying to protect you from",
              "Speaking to the part from a calm, adult ‘Self’ place",
              "Inviting the part to soften, rest, or release what it carries",
            ],
          },
          {
            heading: "Often explored for",
            bullets: [
              "Complex trauma",
              "Inner-child wounds",
              "Shame and self-criticism",
              "Attachment difficulties",
              "Emotional overwhelm",
              "People-pleasing and burnout",
            ],
          },
          {
            heading: "A gentle reminder",
            body:
              "Everyone’s experience of IFS is different. Some people feel parts vividly, others sense them more subtly. There is no right way — your system will share what it is ready to share.",
          },
        ],
      },
      {
        name: "Somatic Experiencing (SE)",
        tagline: "The body knows how to complete what was interrupted.",
        evidence: "🟡 Emerging Evidence",
        developedBy: "Dr. Peter Levine",
        sections: [
          {
            heading: "What is it?",
            body:
              "Somatic Experiencing focuses on the body’s responses to stress and trauma. Rather than talking only about what happened, it helps you notice physical sensations, nervous system activation, tension, and patterns of protection that may still be present long after the event.",
          },
          {
            heading: "How does it work?",
            body:
              "Peter Levine observed that animals experience threat regularly but rarely stay traumatized. He proposed that trauma is not simply what happened to us — it is what remains held inside the nervous system afterwards. SE gently helps fight, flight, freeze, or protective responses that were interrupted finally complete, in small, manageable doses.",
          },
          {
            heading: "What a session may involve",
            bullets: [
              "Tracking sensations like warmth, tingling, tightness, or settling",
              "Grounding and orienting to the room",
              "Pendulating between activation and calm in small waves",
              "Allowing micro-movements the body wants to make",
              "Pausing often, never pushing past what feels manageable",
            ],
          },
          {
            heading: "Often explored for",
            bullets: [
              "Trauma and PTSD",
              "Chronic stress",
              "Anxiety and panic",
              "Freeze and shutdown responses",
              "Nervous system dysregulation",
            ],
          },
          {
            heading: "A gentle reminder",
            body:
              "Body-based work moves slowly on purpose. Less is more. People often report feeling calmer, safer, more grounded, and less reactive over time — but your pace is the right pace.",
          },
        ],
      },
      {
        name: "Trauma-Focused Cognitive Behavioural Therapy (TF-CBT)",
        tagline: "Gently reshape the thoughts trauma left behind.",
        evidence: "🟢 Evidence-Based",
        developedBy: "Judith Cohen, Anthony Mannarino & Esther Deblinger",
        sections: [
          {
            heading: "What is it?",
            body: "TF-CBT combines traditional cognitive behavioural techniques with trauma-specific interventions.",
          },
          {
            heading: "How does it work?",
            body: "The therapist helps individuals:",
            bullets: [
              "Understand trauma reactions",
              "Build coping skills",
              "Challenge unhelpful beliefs",
              "Process traumatic experiences",
              "Strengthen emotional regulation",
            ],
          },
          {
            heading: "What might change?",
            body:
              "People often develop a healthier understanding of themselves, their experiences, and their ability to cope.",
          },
          {
            heading: "Commonly used for",
            bullets: ["PTSD", "Childhood trauma", "Anxiety", "Depression"],
          },
        ],
      },
      {
        name: "Sensorimotor Psychotherapy",
        tagline: "Body, emotion, and meaning — woven together.",
        evidence: "🟡 Emerging Evidence",
        developedBy: "Pat Ogden",
        sections: [
          {
            heading: "What is it?",
            body: "Sensorimotor Psychotherapy bridges neuroscience, attachment theory, and body awareness.",
          },
          {
            heading: "How does it work?",
            body: "Rather than focusing solely on thoughts or memories, therapy explores:",
            bullets: ["Posture", "Movement", "Physical sensations", "Emotional experiences"],
          },
          {
            heading: "Note",
            body: "The body is viewed as an important source of information about unresolved trauma.",
          },
          {
            heading: "Commonly used for",
            bullets: ["Complex PTSD", "Developmental trauma", "Dissociation", "Attachment wounds"],
          },
        ],
      },
      {
        name: "Brainspotting",
        tagline: "Where you look affects how you feel.",
        evidence: "🟡 Emerging Evidence",
        developedBy: "Dr. David Grand",
        sections: [
          { heading: "What is it?", body: "Brainspotting evolved from observations made during EMDR sessions." },
          {
            heading: "How does it work?",
            body:
              "Specific eye positions appear to access deeper emotional and neurological processing. The therapist helps identify a \"brainspot\" connected to an emotional issue while maintaining focused awareness.",
          },
          {
            heading: "Commonly used for",
            bullets: ["Trauma", "PTSD", "Anxiety", "Performance issues", "Emotional blocks"],
          },
        ],
      },
    ],
  },
  {
    emoji: "💛",
    title: "EMOTIONAL HEALING & RELATIONSHIPS",
    intro:
      "These therapies focus on emotions, relationships, self-worth, and long-standing patterns developed throughout life.",
    items: [
      {
        name: "Dialectical Behaviour Therapy (DBT)",
        tagline: "Skills to ride the waves of intense emotion.",
        evidence: "🟢 Evidence-Based",
        developedBy: "Dr. Marsha Linehan",
        sections: [
          {
            heading: "What is it?",
            body:
              "DBT helps people develop practical skills for managing overwhelming emotions, navigating relationships, coping with distress, and building a life that feels worth living. It was originally developed for people experiencing intense emotions and chronic emotional suffering, and is now used widely for many forms of emotional pain.",
          },
          {
            heading: "How does it work?",
            body:
              "DBT balances acceptance (your feelings make sense given what you have lived through) with change (you can also learn new ways of responding). You may learn techniques for calming emotional storms, setting healthy boundaries, tolerating difficult feelings, and responding rather than reacting in stressful situations.",
          },
          {
            heading: "Core skills",
            bullets: [
              "Mindfulness",
              "Distress Tolerance",
              "Emotional Regulation",
              "Interpersonal Effectiveness",
            ],
          },
          {
            heading: "What a session may involve",
            bullets: [
              "Practising a specific skill (e.g. grounding, TIPP, opposite action)",
              "Reviewing a ‘diary card’ of emotions and urges from the week",
              "Walking through a recent difficult moment, step by step",
              "Role-playing boundary or communication scripts",
              "Sometimes a separate weekly skills group alongside 1:1 therapy",
            ],
          },
          {
            heading: "Often explored for",
            bullets: [
              "Intense emotions and emotional overwhelm",
              "Relationship difficulties",
              "Self-destructive coping patterns",
              "Complex trauma recovery",
              "Borderline / emotionally sensitive patterns",
            ],
          },
          {
            heading: "A gentle reminder",
            body:
              "DBT is skill-based, but it is not about ‘fixing’ you. Your emotions make sense. Skills are simply tools you can pick up when they help, and put down when they don’t.",
          },
        ],
      },
      {
        name: "Attachment-Based Therapy",
        tagline: "Healing the bonds that shaped you.",
        evidence: "🟡 Emerging Evidence",
        developedBy: "Based on John Bowlby & Mary Ainsworth's attachment research",
        sections: [
          {
            heading: "What is it?",
            body: "Attachment theory explores how early relationships influence later emotional experiences.",
          },
          {
            heading: "Focus Areas",
            bullets: ["Trust", "Safety", "Intimacy", "Boundaries", "Abandonment fears"],
          },
          {
            heading: "Commonly used for",
            bullets: ["Relationship challenges", "Childhood trauma", "Attachment wounds"],
          },
        ],
      },
      {
        name: "Schema Therapy",
        tagline: "Rewriting the old stories you live by.",
        evidence: "🟢 Evidence-Based",
        developedBy: "Dr. Jeffrey Young",
        sections: [
          {
            heading: "What is it?",
            body: "Schema Therapy helps identify deeply rooted emotional patterns called schemas.",
          },
          {
            heading: "Examples include",
            bullets: ["Abandonment", "Defectiveness", "Mistrust", "Emotional deprivation"],
          },
          {
            heading: "Commonly used for",
            bullets: ["Complex PTSD", "Personality disorders", "Chronic relationship difficulties"],
          },
        ],
      },
      {
        name: "Compassion-Focused Therapy (CFT)",
        tagline: "Meeting yourself with the kindness you deserve.",
        evidence: "🟢 Evidence-Based",
        developedBy: "Professor Paul Gilbert",
        sections: [
          {
            heading: "What is it?",
            body: "CFT was created to help individuals struggling with shame and self-criticism.",
          },
          {
            heading: "Focus",
            body: "Building a kinder internal voice and increasing feelings of safety and self-acceptance.",
          },
          {
            heading: "Commonly used for",
            bullets: ["Shame", "Trauma", "Anxiety", "Depression"],
          },
        ],
      },
      {
        name: "Acceptance and Commitment Therapy (ACT)",
        tagline: "Make room for what's hard, move toward what matters.",
        evidence: "🟢 Evidence-Based",
        developedBy: "Dr. Steven Hayes",
        sections: [
          {
            heading: "What is it?",
            body:
              "ACT teaches people how to stop fighting difficult emotions and instead focus on living according to their values.",
          },
          {
            heading: "Core Principles",
            bullets: ["Acceptance", "Defusion", "Present-moment awareness", "Values", "Committed action"],
          },
          {
            heading: "Commonly used for",
            bullets: ["Anxiety", "Trauma", "Depression", "Chronic pain"],
          },
        ],
      },
    ],
  },
  {
    emoji: "🌿",
    title: "NERVOUS SYSTEM & BODY-BASED APPROACHES",
    intro: "These approaches focus on helping the body feel safer, calmer, and more regulated.",
    items: [
      {
        name: "Polyvagal-Informed Therapy",
        tagline: "Understanding the language of your nervous system.",
        evidence: "🟡 Emerging Evidence",
        developedBy: "Developed from the work of Dr. Stephen Porges",
        sections: [
          {
            heading: "What is it?",
            body: "Polyvagal Theory explains how the nervous system responds to safety, danger, and life threat.",
          },
          {
            heading: "Focus",
            body:
              "Helping individuals recognize nervous system states and increase feelings of safety and connection.",
          },
        ],
      },
      {
        name: "Mindfulness-Based Therapy",
        tagline: "Coming home to the present moment.",
        evidence: "🟢 Evidence-Based",
        sections: [
          { heading: "What is it?", body: "Mindfulness teaches awareness of the present moment without judgment." },
          {
            heading: "Benefits",
            bullets: ["Reduced anxiety", "Improved emotional regulation", "Increased self-awareness"],
          },
        ],
      },
      {
        name: "Trauma-Sensitive Yoga",
        tagline: "Safely re-inhabiting the body.",
        evidence: "🟡 Emerging Evidence",
        sections: [
          {
            heading: "What is it?",
            body: "A trauma-informed adaptation of yoga that emphasizes choice, safety, and body awareness.",
          },
          {
            heading: "Benefits",
            bullets: ["Reconnection with the body", "Reduced dissociation", "Improved nervous system regulation"],
          },
        ],
      },
      {
        name: "Breathwork",
        tagline: "The breath as a doorway to calm.",
        evidence: "🟡 Emerging Evidence",
        sections: [
          {
            heading: "What is it?",
            body: "Intentional breathing techniques designed to influence the nervous system.",
          },
          {
            heading: "Benefits",
            bullets: ["Reduced stress", "Improved emotional regulation", "Increased grounding"],
          },
        ],
      },
    ],
  },
  {
    emoji: "🎨",
    title: "CREATIVE & EXPRESSIVE THERAPIES",
    intro: "For some people, healing happens through expression rather than conversation.",
    items: [
      {
        name: "Art Therapy",
        tagline: "When words aren't enough, let color speak.",
        evidence: "🔵 Complementary Approach",
        sections: [
          {
            heading: "What is it?",
            body:
              "Healing through drawing, painting, sculpture, and creative expression. Often helpful when experiences feel difficult to put into words.",
          },
        ],
      },
      {
        name: "Music Therapy",
        tagline: "Sound that holds what words cannot.",
        evidence: "🟢 Evidence-Based",
        sections: [
          {
            heading: "What is it?",
            body:
              "Uses music, rhythm, songwriting, and listening experiences to support emotional healing and wellbeing. Commonly used in hospitals, trauma programs, and mental health services worldwide.",
          },
        ],
      },
      {
        name: "Dance & Movement Therapy",
        tagline: "Letting the body tell its story.",
        evidence: "🟡 Emerging Evidence",
        sections: [
          {
            heading: "What is it?",
            body: "Uses movement and body expression to support emotional processing and self-awareness.",
          },
        ],
      },
      {
        name: "Drama Therapy",
        tagline: "Healing through story and role.",
        evidence: "🟡 Emerging Evidence",
        sections: [
          {
            heading: "What is it?",
            body: "Uses storytelling, role-play, and creative exploration to facilitate healing and growth.",
          },
        ],
      },
      {
        name: "Creative Writing Therapy",
        tagline: "Finding clarity through writing and reflection.",
        evidence: "🔵 Complementary Approach",
        sections: [
          {
            heading: "What is it?",
            body:
              "Uses journaling, poetry, storytelling, and reflective writing to process emotions and experiences.",
          },
        ],
      },
    ],
  },
  {
    emoji: "🌎",
    title: "HOLISTIC & INTEGRATIVE APPROACHES",
    intro: "These approaches are often used alongside therapy and may support overall wellbeing.",
    items: [
      {
        name: "Nature-Based Therapy",
        tagline: "Finding healing through connection with the natural world.",
        evidence: "🟡 Emerging Evidence",
        sections: [
          {
            heading: "What is it?",
            body: "Healing through connection with nature, outdoor experiences, and natural environments.",
          },
        ],
      },
      {
        name: "Equine Therapy",
        tagline: "Building trust, regulation, and confidence through work with horses.",
        evidence: "🟡 Emerging Evidence",
        sections: [
          {
            heading: "What is it?",
            body:
              "Working alongside horses to build trust, emotional awareness, confidence, and regulation.",
          },
        ],
      },
      {
        name: "Meditation Coaching",
        tagline: "Developing awareness, presence, and self-compassion.",
        evidence: "🔵 Complementary Approach",
        sections: [
          {
            heading: "What is it?",
            body: "Guided practices that support mindfulness, self-awareness, and emotional balance.",
          },
        ],
      },
      {
        name: "Energy Healing",
        tagline: "Supporting wellbeing through holistic energy-based practices.",
        evidence: "🔵 Complementary Approach",
        sections: [
          {
            heading: "What is it?",
            body:
              "Holistic practices focused on relaxation, stress reduction, and wellbeing. Research remains limited compared to evidence-based psychotherapies.",
          },
        ],
      },
      {
        name: "Reiki",
        tagline: "Gentle energy work focused on relaxation and balance.",
        evidence: "🔵 Complementary Approach",
        sections: [
          {
            heading: "What is it?",
            body:
              "A gentle energy-based practice intended to promote relaxation and calm. Many people find Reiki supportive, though scientific evidence remains limited compared to established psychotherapy approaches.",
          },
        ],
      },
    ],
  },
];

function HubCard({
  to,
  icon: Icon,
  color,
  title,
  desc,
}: {
  to: string;
  icon: typeof Brain;
  color: string;
  title: string;
  desc: string;
}) {
  const isHash = to.startsWith("#");
  const inner = (
    <>
      <span
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold-light/60"
        style={{ color }}
      >
        <Icon className="h-5 w-5" strokeWidth={2.25} />
      </span>
      <span className="flex-1">
        <span className="block font-display text-lg text-foreground group-hover:text-[var(--gold-deep)]">
          {title}
        </span>
        <span className="mt-0.5 block text-sm leading-snug text-muted-foreground">{desc}</span>
      </span>
      <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-[var(--gold-deep)] transition-transform group-hover:translate-x-0.5" />
    </>
  );
  const className =
    "group flex items-start gap-3 rounded-2xl border border-gold/25 bg-card/80 p-5 shadow-sm transition-all hover:border-gold/50 hover:shadow-[var(--shadow-gold)]";
  if (isHash) {
    return (
      <a href={to} className={className}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className={className}>
      {inner}
    </Link>
  );
}

function ModalityCard({ m }: { m: Modality }) {
  const [open, setOpen] = useState(false);
  return (
    <article className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm transition-all hover:border-gold/40 hover:shadow-[var(--shadow-gold)]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-start gap-3 p-5 text-start"
      >
        <div className="flex-1">
          <h3 className="font-display text-lg text-foreground">{m.name}</h3>
          {m.tagline && (
            <p className="mt-1 text-sm italic leading-snug text-muted-foreground">
              {m.tagline}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            {m.evidence && (
              <span className="rounded-full bg-gold/10 px-2.5 py-0.5 font-medium text-[var(--gold-deep)]">
                {m.evidence}
              </span>
            )}
            {m.developedBy && (
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground/70">Developed by:</span> {m.developedBy}
              </span>
            )}
          </div>
        </div>
        <ChevronDown
          className={`mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="space-y-4 border-t border-border/50 bg-background/40 px-5 py-5">
          {m.sections.map((s, i) => (
            <div key={i}>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
                {s.heading}
              </h4>
              {s.body && (
                <p className="text-sm leading-relaxed text-foreground/90">{s.body}</p>
              )}
              {s.bullets && (
                <ul className="mt-1.5 space-y-1 text-sm text-foreground/85">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex gap-2">
                      <span aria-hidden className="text-[var(--gold-deep)]">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          {!m.sections.some((s) => /reminder/i.test(s.heading)) && (
            <div className="rounded-xl border border-gold/25 bg-cream/40 px-3 py-2.5">
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
                A gentle reminder
              </h4>
              <p className="text-sm leading-relaxed text-foreground/85">
                Everyone&rsquo;s experience is different. There is no single &ldquo;right&rdquo; way to feel
                during or after a session. What matters is finding an approach — and a practitioner —
                where your pace is respected and your nervous system feels safer over time.
              </p>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function StatTile({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: number | string;
  label: string;
}) {
  return (
    <div className="rounded-2xl border border-gold/20 bg-background/60 p-4 text-center">
      <div className="mx-auto mb-1 flex h-8 w-8 items-center justify-center rounded-full bg-cream/60 text-[var(--gold-deep)]">
        {icon}
      </div>
      <div className="font-serif text-2xl text-foreground">{value}</div>
      <div className="text-[10px] font-medium uppercase tracking-wider text-foreground">
        {label}
      </div>
    </div>
  );
}

function TherapyApproachesPage() {
  const [activeCat, setActiveCat] = useState<string>("all");

  const totals = useMemo(() => {
    const all = CATEGORIES.flatMap((c) => c.items);
    return {
      total: all.length,
      evidence: all.filter((m) => m.evidence === "🟢 Evidence-Based").length,
      emerging: all.filter((m) => m.evidence === "🟡 Emerging Evidence").length,
      categories: CATEGORIES.length,
    };
  }, []);

  const visibleCats = useMemo(
    () => (activeCat === "all" ? CATEGORIES : CATEGORIES.filter((c) => c.title === activeCat)),
    [activeCat],
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <header className="mb-8 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-xs font-medium text-foreground">
          <span aria-hidden>🌿</span> Therapy & Self-Reflection Tools
        </div>
        <h1 className="font-display text-4xl tracking-tight text-foreground md:text-5xl">
          💛 There Is No One Right Way To Heal
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          Explore evidence-based therapies, nervous system approaches, wellbeing check-ins,
          and professional support — at your own pace.
        </p>
      </header>

      {/* ── Therapy & Support ───────────────────────── */}
      <section aria-labelledby="therapy-support-heading" className="mb-10">
        <div className="mb-4 px-1 text-center">
          <h2 id="therapy-support-heading" className="font-display text-2xl text-foreground md:text-3xl">
            🧠 Therapy &amp; Support
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
            Explore therapy approaches, find therapists, and access self-reflection tools.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            { to: "#approaches", emoji: "🧠", label: "Therapy Approaches", sub: "Evidence-based methods." },
            { to: "/therapists", emoji: "🩺", label: "Therapist Directory", sub: "Find trusted support." },
          ].map((c) => (
            <a
              key={c.label}
              href={c.to}
              className="group flex h-full flex-col gap-1 rounded-2xl border border-gold/25 bg-card p-4 transition hover:-translate-y-0.5 hover:border-gold/55 hover:bg-cream/40 hover:shadow-[var(--shadow-gold)]"
            >
              <span aria-hidden className="text-xl">{c.emoji}</span>
              <span className="font-display text-sm text-foreground leading-snug">{c.label}</span>
              <span className="text-xs text-muted-foreground">{c.sub}</span>
            </a>
          ))}
        </div>
      </section>



      {/* Dashboard summary */}
      <section className="mb-8 overflow-hidden rounded-3xl border border-gold/40 bg-gradient-to-br from-card via-card to-cream/40 p-6 shadow-[var(--shadow-gold)]">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <span className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-[var(--gold-deep)]">
              <Sparkles className="h-3 w-3" />
              Therapies overview
            </span>
            <h2 className="mt-2 font-serif text-2xl text-foreground sm:text-3xl">
              Explore at your pace
            </h2>
          </div>
          <p className="max-w-xs text-xs text-muted-foreground">
            Tap any therapy below to expand its details. Filter by category to narrow your view.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile icon={<Layers className="h-4 w-4" />} value={totals.total} label="Therapies" />
          <StatTile icon={<ShieldCheck className="h-4 w-4" />} value={totals.evidence} label="Evidence-based" />
          <StatTile icon={<Sparkles className="h-4 w-4" />} value={totals.emerging} label="Emerging" />
          <StatTile icon={<Brain className="h-4 w-4" />} value={totals.categories} label="Categories" />
        </div>
      </section>


      {/* Category filter pills */}
      <div className="mb-8 flex flex-wrap justify-center gap-2">
        <button
          onClick={() => setActiveCat("all")}
          className={[
            "rounded-full border px-4 py-2 text-sm font-medium transition-all",
            activeCat === "all"
              ? "border-transparent bg-foreground text-background"
              : "border-border bg-card text-muted-foreground hover:text-foreground",
          ].join(" ")}
        >
          All
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c.title}
            onClick={() => setActiveCat(c.title)}
            className={[
              "rounded-full border px-4 py-2 text-sm font-medium transition-all",
              activeCat === c.title
                ? "border-transparent bg-foreground text-background"
                : "border-border bg-card text-muted-foreground hover:text-foreground",
            ].join(" ")}
          >
            <span aria-hidden className="me-1">{c.emoji}</span>
            {c.title.split(" ")[0]}
          </button>
        ))}
      </div>

      <div id="approaches" className="space-y-14 scroll-mt-24">
        {visibleCats.map((cat) => (
          <section key={cat.title}>
            <h2 className="font-display text-2xl text-foreground md:text-3xl">
              <span aria-hidden className="me-2">{cat.emoji}</span>
              {cat.title}
            </h2>
            {cat.intro && (
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                {cat.intro}
              </p>
            )}
            <div className="mt-6 grid items-start gap-3 md:grid-cols-2">
              {cat.items.map((m) => (
                <ModalityCard key={m.name} m={m} />
              ))}
            </div>
          </section>
        ))}

        {/* Closing note */}
        <section className="rounded-3xl border border-gold/40 bg-gradient-to-br from-card via-card to-cream/40 p-8 shadow-[var(--shadow-gold)]">
          <h2 className="font-display text-2xl text-foreground md:text-3xl">
            <span aria-hidden className="me-2">💡</span>
            Important Note
          </h2>
          <p className="mt-3 text-base font-medium text-foreground">
            No single therapy works for everyone.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            Many trauma survivors benefit from combining approaches, such as:
          </p>
          <ul className="mt-3 space-y-1.5 text-sm text-foreground/85">
            {[
              "EMDR + IFS",
              "Somatic Experiencing + Attachment Therapy",
              "DBT + Trauma-Focused CBT",
              "Psychodynamic Therapy + Mindfulness",
              "Art Therapy + Traditional Psychotherapy",
            ].map((c) => (
              <li key={c} className="flex gap-2">
                <span aria-hidden className="text-[var(--gold-deep)]">•</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm italic leading-relaxed text-muted-foreground">
            Healing is not about finding the "perfect" therapy. It is about finding approaches,
            relationships, and tools that help you feel safer, more connected, and more able to
            live the life you want.
          </p>
        </section>
      </div>

      <div className="mt-12 text-center text-sm">
        <Link to="/library" className="text-muted-foreground hover:text-foreground">
          ← Back to the Recovery Library
        </Link>
      </div>
    </div>
  );
}

